// Unified AI response parser (refactor 2.1 — PLAN-REFACTOR.md)
// Replaces the three overlapping parsers previously living in aiService.ts:
// parseResponse, extractMarkdownSections, extractSectionsFromText.
// Section logic is moved 1:1 — this is a refactor, not a redesign.
//
// Priority chain: markdown (preferred) → JSON (backward compat) → text → raw.
// Raw fallback fires ONLY when no structure was detected anywhere.

export interface AIResponse {
  general_assessment: string;
  strengths: string;
  areas_for_support: string;
  classroom_implications: string;
  teacher_recommendations: string[] | string;
  parent_recommendations: string[] | string;
}

export type ParserKind = 'markdown' | 'json' | 'text' | 'raw';

export interface ParseResult {
  sections: AIResponse;
  parser: ParserKind;
  warnings: string[];
}

function emptySections(): AIResponse {
  return {
    general_assessment: '',
    strengths: '',
    areas_for_support: '',
    classroom_implications: '',
    teacher_recommendations: [],
    parent_recommendations: []
  };
}

// Buduje regex nagłówka sekcji markdown (BLOKER 3b — realne warianty gemini-2.5-flash):
// - poziom ## lub ###
// - opcjonalna numeracja ("3." / "3)") przed i/lub po emoji
// - opcjonalne emoji (w tym sekwencje ZWJ jak 👨‍🏫 i selektory wariantu)
// - case-insensitive, dopasowanie od początku linii
function sectionHeader(body: string): RegExp {
  return new RegExp(
    String.raw`^#{2,3}\s*(?:\d+[.)]\s*)?(?:[\p{Extended_Pictographic}\u200D\uFE0F]+\s*)?(?:\d+[.)]\s*)?(?:${body})`,
    'imu'
  );
}

export class ResponseParser {
  parse(text: string): ParseResult {
    const warnings: string[] = [];

    // 1. Try Markdown sections first (new format)
    // BLOKER 3a: ścieżka markdown wygrywa, gdy znaleziono JAKIKOLWIEK znany
    // nagłówek sekcji z treścią — nie tylko ogólną ocenę. Wcześniej odpowiedź
    // bez "## Ogólna ocena" spadała aż do raw fallbacku i gubiła sekcje 2-6.
    const markdownResult = this.extractMarkdownSections(text);
    if (this.hasAnyContent(markdownResult)) {
      console.info('[parser] succeeded via', 'markdown');
      return { sections: markdownResult, parser: 'markdown', warnings };
    }

    // 2. Try JSON (backward compat)
    try {
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        if (parsed.general_assessment) {
          console.info('[parser] succeeded via', 'json');
          return {
            sections: {
              general_assessment: parsed.general_assessment || '',
              strengths: parsed.strengths || '',
              areas_for_support: parsed.areas_for_support || parsed.difficulties || '',
              classroom_implications: parsed.classroom_implications || parsed.school_functioning || '',
              teacher_recommendations: parsed.teacher_recommendations || [],
              parent_recommendations: parsed.parent_recommendations || []
            },
            parser: 'json',
            warnings
          };
        }
      }
    } catch (error) {
      console.warn('⚠️ parseResponse: JSON parse failed, trying text extraction');
      warnings.push('JSON parse failed, trying text extraction');
    }

    // 3. Text extraction fallback
    const textResult = this.extractSectionsFromText(text);
    if (textResult.general_assessment) {
      console.info('[parser] succeeded via', 'text');
      return { sections: textResult, parser: 'text', warnings };
    }

    // 4. Nuclear fallback - NEVER lose content
    // NOTE: QA monitoruje ten komunikat — nie zmieniać treści.
    console.warn('⚠️ parseResponse: All parsing failed, using full text as general_assessment');
    warnings.push('All parsing failed, using full text as general_assessment');
    return {
      sections: { ...emptySections(), general_assessment: text.trim() },
      parser: 'raw',
      warnings
    };
  }

  private extractMarkdownSections(text: string): AIResponse {
    const sections: AIResponse = emptySections();

    // Match ##/### headers (with optional emoji/numbering — see sectionHeader()).
    // BLOKER 3b: poszerzone o realne synonimy nagłówków gemini-2.5-flash.
    // UWAGA: "ogólna ocena rozwoju" jest pokryta prefiksem "ogólna ocena" —
    // sufiks "rozwoju" celowo zostaje w treści (nagrane zachowanie, case 1).
    const sectionMap: Array<{ patterns: RegExp[]; key: keyof AIResponse }> = [
      {
        patterns: [
          sectionHeader(String.raw`og[óo]lna ocena|ocena og[óo]lna|podsumowanie wynik[óo]w`),
          sectionHeader(String.raw`general assessment`)
        ],
        key: 'general_assessment'
      },
      {
        patterns: [
          sectionHeader(String.raw`(?:funkcjonalne\s+)?mocne strony|silne strony|zasoby dziecka`),
          sectionHeader(String.raw`strengths`)
        ],
        key: 'strengths'
      },
      {
        patterns: [
          sectionHeader(String.raw`obszary (?:wymagaj[ąa]ce wsparcia|trudno[śs]ci|do wsparcia)|trudno[śs]ci|s[łl]abe strony`),
          sectionHeader(String.raw`areas for support|difficulties`)
        ],
        key: 'areas_for_support'
      },
      {
        patterns: [
          sectionHeader(String.raw`funkcjonowanie (?:szkolne|w szkole|w klasie)|implikacje szkolne`),
          sectionHeader(String.raw`classroom implications|school functioning`)
        ],
        key: 'classroom_implications'
      },
      {
        patterns: [
          sectionHeader(String.raw`zalecenia dla nauczycieli|rekomendacje dla nauczycieli|wskaz[óo]wki (?:dla nauczycieli|dydaktyczne)`),
          sectionHeader(String.raw`teacher recommendations`)
        ],
        key: 'teacher_recommendations'
      },
      {
        patterns: [
          sectionHeader(String.raw`zalecenia dla rodzic[óo]w|rekomendacje dla rodzic[óo]w|wskaz[óo]wki dla rodzic[óo]w|wsparcie (?:domowe|w domu)`),
          sectionHeader(String.raw`parent recommendations`)
        ],
        key: 'parent_recommendations'
      }
    ];

    // Find all section positions
    const positions: Array<{ key: keyof AIResponse; start: number; headerEnd: number }> = [];

    for (const section of sectionMap) {
      for (const pattern of section.patterns) {
        const match = text.match(pattern);
        if (match && match.index !== undefined) {
          positions.push({
            key: section.key,
            start: match.index,
            headerEnd: match.index + match[0].length
          });
          break;
        }
      }
    }

    if (positions.length === 0) return sections;

    // Sort by position
    positions.sort((a, b) => a.start - b.start);

    // Extract content between headers
    for (let i = 0; i < positions.length; i++) {
      const contentStart = positions[i].headerEnd;
      const contentEnd = i + 1 < positions.length ? positions[i + 1].start : text.length;
      const content = text.slice(contentStart, contentEnd).trim();

      const key = positions[i].key;
      if (key === 'teacher_recommendations' || key === 'parent_recommendations') {
        // Extract list items
        const items = content
          .split('\n')
          .map(line => line.replace(/^[\s]*[-•*]\s*/, '').trim())
          .filter(line => line.length > 0);
        sections[key] = items.length > 0 ? items : [content];
      } else {
        (sections[key] as string) = content;
      }
    }

    // BLOKER 3a: tekst sprzed pierwszego znanego nagłówka (preambuła) trafia do
    // general_assessment, jeśli sekcja ogólnej oceny nie wystąpiła jawnie.
    // Gdy wystąpiła — preambuła (np. wyciek thinkingu) jest odrzucana jak dotąd (case 12).
    if (!positions.some(p => p.key === 'general_assessment')) {
      const preamble = text.slice(0, positions[0].start).trim();
      if (preamble) {
        sections.general_assessment = preamble;
      }
    }

    return sections;
  }

  // Czy parser markdown znalazł cokolwiek z treścią (dowolna sekcja)?
  private hasAnyContent(sections: AIResponse): boolean {
    return Object.values(sections).some(value =>
      (Array.isArray(value) ? value.join('') : value).trim().length > 0
    );
  }

  private extractSectionsFromText(text: string): AIResponse {
    const sections: AIResponse = emptySections();

    // Simple text extraction logic
    const lines = text.split('\n');
    let currentSection: keyof AIResponse | '' = '';
    let currentContent: string[] = [];

    for (const line of lines) {
      const trimmed = line.trim();

      if (trimmed.toLowerCase().includes('ogólna ocena') || trimmed.toLowerCase().includes('general assessment')) {
        if (currentSection) {
          this.assignSection(sections, currentSection, currentContent);
        }
        currentSection = 'general_assessment';
        currentContent = [];
      } else if (trimmed.toLowerCase().includes('mocne strony') || trimmed.toLowerCase().includes('strengths')) {
        if (currentSection) {
          this.assignSection(sections, currentSection, currentContent);
        }
        currentSection = 'strengths';
        currentContent = [];
      } else if (trimmed.toLowerCase().includes('trudności') || trimmed.toLowerCase().includes('difficulties') || trimmed.toLowerCase().includes('areas_for_support')) {
        if (currentSection) {
          this.assignSection(sections, currentSection, currentContent);
        }
        currentSection = 'areas_for_support';
        currentContent = [];
      } else if (trimmed.toLowerCase().includes('funkcjonowanie szkolne') || trimmed.toLowerCase().includes('school functioning') || trimmed.toLowerCase().includes('classroom_implications')) {
        if (currentSection) {
          this.assignSection(sections, currentSection, currentContent);
        }
        currentSection = 'classroom_implications';
        currentContent = [];
      } else if (trimmed.toLowerCase().includes('zalecenia dla nauczycieli') || trimmed.toLowerCase().includes('teacher recommendations')) {
        if (currentSection) {
          this.assignSection(sections, currentSection, currentContent);
        }
        currentSection = 'teacher_recommendations';
        currentContent = [];
      } else if (trimmed.toLowerCase().includes('zalecenia dla rodziców') || trimmed.toLowerCase().includes('parent recommendations')) {
        if (currentSection) {
          this.assignSection(sections, currentSection, currentContent);
        }
        currentSection = 'parent_recommendations';
        currentContent = [];
      } else if (trimmed && currentSection) {
        currentContent.push(trimmed);
      }
    }

    // Don't forget the last section
    if (currentSection) {
      this.assignSection(sections, currentSection, currentContent);
    }

    return sections;
  }

  private assignSection(sections: AIResponse, sectionName: keyof AIResponse, content: string[]): void {
    if (sectionName === 'teacher_recommendations' || sectionName === 'parent_recommendations') {
      sections[sectionName] = content.filter(line => line.startsWith('-') || line.startsWith('•') || /^\d+\./.test(line));
    } else {
      sections[sectionName] = content.join(' ').trim();
    }
  }
}

export const responseParser = new ResponseParser();
export default responseParser;
